import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  DocumentData,
} from 'firebase/firestore';
import { getErrorCode } from '@/utils/errors';
import {
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { User } from 'firebase/auth';
import { db } from '@/lib/firebase';
import { createProfileUpdateActivity } from './activityService';
import { hasGoogleAvatar, normalizeGoogleAvatarUrl } from '@/utils/avatars';

export interface UserProfile {
  displayName: string;
  email: string;
  avatarId?: number;
  customAvatarUrl?: string;
  useGoogleAvatar?: boolean;
  authMethod: 'email' | 'google' | 'anonymous';
  updatedAt: Date;
}

/**
 * Gets user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        displayName: data.displayName || '',
        email: data.email || '',
        avatarId: data.avatarId,
        customAvatarUrl: data.customAvatarUrl,
        useGoogleAvatar: data.useGoogleAvatar,
        authMethod: data.authMethod || 'email',
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

/**
 * Updates user profile in Firestore and Firebase Auth
 */
export async function updateUserProfile(
  user: User,
  profileData: Partial<Pick<UserProfile, 'displayName' | 'email' | 'avatarId' | 'customAvatarUrl' | 'useGoogleAvatar'>>
): Promise<void> {
  try {
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);

    // Determine auth method
    let authMethod: 'email' | 'google' | 'anonymous' = 'anonymous';
    if (user.providerData.length > 0) {
      const provider = user.providerData[0].providerId;
      if (provider === 'google.com') {
        authMethod = 'google';
      } else if (provider === 'password') {
        authMethod = 'email';
      }
    }

    // Filter out undefined values to avoid Firestore errors
    const updatedData: Record<string, unknown> = {
      authMethod,
      updatedAt: new Date(),
    };

    // Handle avatar fields with proper clearing logic
    if (profileData.displayName !== undefined) {
      updatedData.displayName = profileData.displayName;
    }
    if (profileData.email !== undefined) {
      updatedData.email = profileData.email;
    }

    // Avatar handling - clear conflicting fields
    if (profileData.useGoogleAvatar === true) {
      // When using Google avatar, clear other avatar fields
      updatedData.useGoogleAvatar = true;
      updatedData.avatarId = null; // Explicitly set to null to clear
      updatedData.customAvatarUrl = null; // Explicitly set to null to clear
    } else if (profileData.avatarId !== undefined) {
      // When using random avatar, clear other avatar fields
      updatedData.avatarId = profileData.avatarId;
      updatedData.useGoogleAvatar = false;
      updatedData.customAvatarUrl = null; // Explicitly set to null to clear
    } else if (profileData.customAvatarUrl !== undefined) {
      // When using custom uploaded image, clear other avatar fields
      updatedData.customAvatarUrl = profileData.customAvatarUrl;
      updatedData.useGoogleAvatar = false;
      updatedData.avatarId = null; // Explicitly set to null to clear
    }

    console.log('Profile service - updating with data:', updatedData);

    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(docRef, updatedData as DocumentData);
    } else {
      // Create new document with proper avatar field handling
      const newDocData: Record<string, unknown> = {
        displayName: profileData.displayName || '',
        email: profileData.email || user.email || '',
        authMethod,
        updatedAt: new Date(),
      };

      // Handle avatar fields with proper clearing logic for new documents
      if (profileData.useGoogleAvatar === true) {
        newDocData.useGoogleAvatar = true;
        newDocData.avatarId = null;
        newDocData.customAvatarUrl = null;
      } else if (profileData.avatarId !== undefined) {
        newDocData.avatarId = profileData.avatarId;
        newDocData.useGoogleAvatar = false;
        newDocData.customAvatarUrl = null;
      } else if (profileData.customAvatarUrl !== undefined) {
        newDocData.customAvatarUrl = profileData.customAvatarUrl;
        newDocData.useGoogleAvatar = false;
        newDocData.avatarId = null;
      }

      await setDoc(docRef, newDocData);
    }

    // Update Firebase Auth profile if displayName changed
    if (profileData.displayName !== undefined && profileData.displayName !== user.displayName) {
      await updateProfile(user, {
        displayName: profileData.displayName,
      });
    }

    // Update email in Firebase Auth if changed (only for email auth users)
    if (
      profileData.email !== undefined &&
      profileData.email !== user.email &&
      authMethod === 'email'
    ) {
      await updateEmail(user, profileData.email);
    }

    // Log profile update activity
    try {
      const userName = profileData.displayName || user.displayName || user.email || 'User';
      const userPhotoURL = hasGoogleAvatar(user) ? normalizeGoogleAvatarUrl(user.photoURL!) : undefined;
      const userAvatarId = profileData.avatarId;

      await createProfileUpdateActivity(
        user.uid,
        userName,
        user.isAnonymous,
        userPhotoURL,
        userAvatarId
      );
    } catch (activityError) {
      console.error('Error logging profile update activity:', activityError);
      // Don't throw error for activity logging failure
    }
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw new Error('Failed to update user profile');
  }
}

/**
 * Changes user password (only for email auth users)
 */
export async function changeUserPassword(
  user: User,
  currentPassword: string,
  newPassword: string
): Promise<void> {
  try {
    // Check if user is using email authentication
    const isEmailAuth = user.providerData.some(provider => provider.providerId === 'password');
    if (!isEmailAuth) {
      throw new Error('Password change is only available for email authentication');
    }

    if (!user.email) {
      throw new Error('User email is required for password change');
    }

    // Re-authenticate user with current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('Error changing password:', error);
    const code = getErrorCode(error);

    if (code === 'auth/wrong-password') {
      throw new Error('Current password is incorrect');
    } else if (code === 'auth/weak-password') {
      throw new Error('New password is too weak');
    } else if (code === 'auth/requires-recent-login') {
      throw new Error('Please log out and log back in before changing your password');
    }

    throw new Error('Failed to change password');
  }
}

/**
 * Gets user profile for anonymous users from localStorage
 */
export function getAnonymousUserProfile(userId?: string): Partial<UserProfile> {
  try {
    // If userId is provided, get user-specific profile
    if (userId) {
      // Prefer versioned key; fall back to the legacy unversioned one written before the schema bump.
      const stored =
        localStorage.getItem(`anonymousProfile:v1:${userId}`) ||
        localStorage.getItem(`anonymousProfile_${userId}`);
      if (stored) {
        const data = JSON.parse(stored);
        return {
          displayName: data.displayName || '',
          avatarId: data.avatarId,
          authMethod: 'anonymous',
          updatedAt: new Date(data.updatedAt || Date.now()),
        };
      }
    } else {
      const stored =
        localStorage.getItem('anonymousProfile:v1') ||
        localStorage.getItem('anonymousProfile');
      if (stored) {
        const data = JSON.parse(stored);
        return {
          displayName: data.displayName || '',
          avatarId: data.avatarId,
          authMethod: 'anonymous',
          updatedAt: new Date(data.updatedAt || Date.now()),
        };
      }
    }
  } catch (error) {
    console.error('Error reading anonymous profile from localStorage:', error);
  }

  return {
    displayName: '',
    authMethod: 'anonymous',
    updatedAt: new Date(),
  };
}

/**
 * Updates anonymous user profile in localStorage
 */
export async function updateAnonymousUserProfile(
  profileData: Partial<Pick<UserProfile, 'displayName' | 'avatarId' | 'customAvatarUrl' | 'useGoogleAvatar'>>,
  userId?: string,
  logActivity: boolean = false
): Promise<void> {
  try {
    const current = getAnonymousUserProfile(userId);
    const updated = {
      ...current,
      ...profileData,
      updatedAt: new Date().toISOString(),
    };

    // Versioned key so future schema changes can ignore old payloads instead of crashing.
    if (userId) {
      localStorage.setItem(`anonymousProfile:v1:${userId}`, JSON.stringify(updated));
    } else {
      localStorage.setItem('anonymousProfile:v1', JSON.stringify(updated));
    }

    // Log profile update activity if requested and we have a userId
    if (logActivity && userId) {
      try {
        const userName = profileData.displayName || current.displayName || 'Anonymous User';
        const userAvatarId = profileData.avatarId || current.avatarId;

        await createProfileUpdateActivity(
          userId,
          userName,
          true, // isAnonymous
          undefined, // userPhotoURL
          userAvatarId
        );
      } catch (activityError) {
        console.error('Error logging anonymous profile update activity:', activityError);
        // Don't throw error for activity logging failure
      }
    }
  } catch (error) {
    console.error('Error storing anonymous profile in localStorage:', error);
    throw new Error('Failed to update anonymous profile');
  }
}
